import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { authService } from '../services/authService';
import type { User, AuthResult, LoginCredentials, RegisterCredentials, ResetPasswordData, VerifyEmailData, OAuthProvider } from '../shared/auth-types';

/**
 * AuthContext - Global authentication state management
 *
 * Multi-provider authentication supporting:
 * - Email/password login and registration
 * - Microsoft OAuth (via backend redirect)
 * - Google OAuth (via backend redirect)
 * - Password reset flow
 * - Email verification flow
 */

export interface AuthStatus {
  authenticated: boolean;
  user?: User;
}

interface AuthContextValue {
  /** Current authentication status */
  authStatus: AuthStatus | null;
  /** Whether initial auth check is in progress */
  isLoading: boolean;
  /** Loading state for any auth operation */
  isLoggingIn: boolean;
  /** Error message from last auth operation */
  error: string | null;
  /** Clear error message */
  clearError: () => void;
  /** Refresh user profile from backend */
  refreshUser: () => Promise<void>;

  // Email/password authentication
  /** Login with email and password */
  loginWithEmail: (email: string, password: string) => Promise<AuthResult>;
  /** Register with email and password */
  register: (credentials: RegisterCredentials) => Promise<AuthResult>;

  // OAuth authentication
  /** Initiate Microsoft OAuth login */
  loginWithMicrosoft: () => void;
  /** Initiate Google OAuth login */
  loginWithGoogle: () => void;
  /** Handle OAuth callback with token */
  handleOAuthCallback: (token: string) => Promise<AuthResult>;

  // Password reset
  /** Request password reset email */
  forgotPassword: (email: string) => Promise<AuthResult>;
  /** Reset password with token */
  resetPassword: (data: ResetPasswordData) => Promise<AuthResult>;

  // Email verification
  /** Verify email with token */
  verifyEmail: (data: VerifyEmailData) => Promise<AuthResult>;
  /** Request new verification email */
  requestVerificationEmail: (email: string) => Promise<AuthResult>;

  // Logout
  /** Logout current user */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initial auth check on mount
  useEffect(() => {
    let mounted = true;

    async function checkAuth() {
      try {
        const isAuth = await authService.isAuthenticated();
        const user = await authService.getUser();

        if (mounted) {
          setAuthStatus({
            authenticated: isAuth,
            user: user || undefined
          });
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to check auth status:', err);
        if (mounted) {
          setAuthStatus({ authenticated: false });
          setIsLoading(false);
        }
      }
    }

    checkAuth();

    return () => {
      mounted = false;
    };
  }, []);

  // Subscribe to auth state changes (defer until initial check completes)
  useEffect(() => {
    if (isLoading) return;

    const unsubscribe = authService.onAuthChanged((authenticated, user) => {
      setAuthStatus({ authenticated, user });
    });

    return unsubscribe;
  }, [isLoading]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const user = await authService.refreshUser();
      setAuthStatus(prev => ({
        authenticated: !!user,
        user: user || undefined
      }));
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  }, []);

  // Email/password login
  const loginWithEmail = useCallback(async (email: string, password: string): Promise<AuthResult> => {
    setIsLoggingIn(true);
    setError(null);

    try {
      const result = await authService.loginWithEmail({ email, password });

      if (result.success) {
        if (result.user) {
          // Directly update auth status - don't rely on listener (race condition fix)
          setAuthStatus({
            authenticated: true,
            user: result.user
          });
        } else {
          setAuthStatus({ authenticated: false });
          setError('Login succeeded but no user returned');
        }
      } else {
        setError(result.error || 'Login failed');
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  // Registration
  const register = useCallback(async (credentials: RegisterCredentials): Promise<AuthResult> => {
    setIsLoggingIn(true);
    setError(null);

    try {
      const result = await authService.register(credentials);

      if (!result.success) {
        setError(result.error || 'Registration failed');
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  // Helper to get the intended redirect path after OAuth
  const getOAuthRedirectPath = useCallback((): string => {
    // Helper to validate path is safe relative path
    const isValidRelativePath = (path: string): boolean => {
      if (!path || !path.startsWith('/') || path.startsWith('//')) return false;
      if (path.includes(':')) return false;
      try {
        const testUrl = new URL(path, window.location.origin);
        return testUrl.origin === window.location.origin;
      } catch {
        return false;
      }
    };

    // First priority: check for redirect param in URL (set by RequireAuth)
    const urlParams = new URLSearchParams(window.location.search);
    const redirectParam = urlParams.get('redirect');

    if (redirectParam) {
      const decoded = decodeURIComponent(redirectParam);
      if (isValidRelativePath(decoded) &&
          !decoded.startsWith('/login') &&
          !decoded.startsWith('/auth') &&
          !decoded.startsWith('/register')) {
        return decoded;
      }
    }

    // Second priority: current path if not an auth page
    const currentPath = window.location.pathname;
    if (!currentPath.startsWith('/login') && !currentPath.startsWith('/auth') && !currentPath.startsWith('/register')) {
      return currentPath;
    }

    // Default: home page
    return '/';
  }, []);

  // OAuth login
  const loginWithMicrosoft = useCallback(async () => {
    setIsLoggingIn(true);
    setError(null);

    try {
      // Save intended redirect path for after OAuth
      sessionStorage.setItem('auth_redirect', getOAuthRedirectPath());
      const result = await authService.loginWithOAuth('microsoft');

      if (!result.success) {
        setError(result.error || 'Microsoft OAuth failed');
        setIsLoggingIn(false);
      }
      // If success, page will redirect - don't reset isLoggingIn
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      setIsLoggingIn(false);
    }
  }, [getOAuthRedirectPath]);

  const loginWithGoogle = useCallback(async () => {
    setIsLoggingIn(true);
    setError(null);

    try {
      // Save intended redirect path for after OAuth
      sessionStorage.setItem('auth_redirect', getOAuthRedirectPath());
      const result = await authService.loginWithOAuth('google');

      if (!result.success) {
        setError(result.error || 'Google OAuth failed');
        setIsLoggingIn(false);
      }
      // If success, page will redirect - don't reset isLoggingIn
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      setIsLoggingIn(false);
    }
  }, [getOAuthRedirectPath]);

  // OAuth callback
  const handleOAuthCallback = useCallback(async (token: string): Promise<AuthResult> => {
    setIsLoggingIn(true);
    setError(null);

    try {
      const result = await authService.handleOAuthCallback(token);

      if (result.success) {
        if (result.user) {
          // Directly update auth status - don't rely on listener (race condition fix)
          setAuthStatus({
            authenticated: true,
            user: result.user
          });
        } else {
          setError(result.error || 'OAuth login failed: missing user');
        }
      } else {
        setError(result.error || 'OAuth login failed');
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  // Password reset
  const forgotPassword = useCallback(async (email: string): Promise<AuthResult> => {
    setIsLoggingIn(true);
    setError(null);

    try {
      const result = await authService.forgotPassword(email);

      if (!result.success) {
        setError(result.error || 'Failed to send reset email');
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  const resetPassword = useCallback(async (data: ResetPasswordData): Promise<AuthResult> => {
    setIsLoggingIn(true);
    setError(null);

    try {
      const result = await authService.resetPassword(data);

      if (!result.success) {
        setError(result.error || 'Failed to reset password');
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  // Email verification
  const verifyEmail = useCallback(async (data: VerifyEmailData): Promise<AuthResult> => {
    setIsLoggingIn(true);
    setError(null);

    try {
      const result = await authService.verifyEmail(data);

      if (!result.success) {
        setError(result.error || 'Failed to verify email');
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  const requestVerificationEmail = useCallback(async (email: string): Promise<AuthResult> => {
    setIsLoggingIn(true);
    setError(null);

    try {
      const result = await authService.requestVerificationEmail(email);

      if (!result.success) {
        setError(result.error || 'Failed to send verification email');
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await authService.logout();
      setAuthStatus({ authenticated: false });
      setError(null);
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to logout');
    }
  }, []);

  const value: AuthContextValue = {
    authStatus,
    isLoading,
    isLoggingIn,
    error,
    clearError,
    refreshUser,
    loginWithEmail,
    register,
    loginWithMicrosoft,
    loginWithGoogle,
    handleOAuthCallback,
    forgotPassword,
    resetPassword,
    verifyEmail,
    requestVerificationEmail,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

/**
 * Hook to get authentication status (shorthand)
 * @returns authenticated boolean or null if loading
 */
export function useIsAuthenticated(): boolean | null {
  const { authStatus, isLoading } = useAuth();

  if (isLoading) return null;
  return authStatus?.authenticated ?? false;
}
